import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';
import { FaEdit, FaPlus, FaSchool, FaTrash, FaUserTie } from 'react-icons/fa';
import DataTable from '../../components_shared/DataTable';
import TableToolbar from '../../components_shared/TableToolbar';
import { schoolAPI, userAPI } from '../../utils/api';
import '../../styles/institutional-form.css';

const emptyForm = {
  id: null,
  username: '',
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  phone: '',
  status: 'ACTIVE',
  schoolIds: [],
};

function displayName(user) {
  return [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username;
}

export default function HeadMastersPage() {
  const [headMasters, setHeadMasters] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, schoolsRes] = await Promise.all([
        userAPI.getUsers({ role: 'HEAD_MASTER', page: 0, size: 500 }),
        schoolAPI.getAll(),
      ]);
      setHeadMasters(usersRes.data?.content || usersRes.data || []);
      setSchools(schoolsRes.data || []);
    } catch (err) {
      Swal.fire('Unable to load head masters', err.message || 'Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const assignedByUser = useMemo(() => {
    const map = new Map();
    schools.forEach(school => {
      if (!school.head_master_user_id) return;
      const list = map.get(Number(school.head_master_user_id)) || [];
      list.push(school);
      map.set(Number(school.head_master_user_id), list);
    });
    return map;
  }, [schools]);

  const openCreate = () => {
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = user => {
    setForm({
      id: user.id,
      username: user.username || '',
      email: user.email || '',
      password: '',
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      phone: user.phone || '',
      status: user.status || 'ACTIVE',
      schoolIds: (assignedByUser.get(Number(user.id)) || []).map(school => String(school.id)),
    });
    setModalOpen(true);
  };

  const change = event => setForm(current => ({ ...current, [event.target.name]: event.target.value }));

  const toggleSchool = id => {
    setForm(current => {
      const key = String(id);
      const schoolIds = current.schoolIds.includes(key)
        ? current.schoolIds.filter(item => item !== key)
        : [...current.schoolIds, key];
      return { ...current, schoolIds };
    });
  };

  const saveAssignments = async userId => {
    const selected = new Set(form.schoolIds.map(String));
    const currentlyAssigned = schools.filter(school => Number(school.head_master_user_id) === Number(userId));
    const changes = [];

    schools.forEach(school => {
      const shouldAssign = selected.has(String(school.id));
      const assignedToThisUser = Number(school.head_master_user_id) === Number(userId);
      if (shouldAssign && !assignedToThisUser) {
        changes.push(schoolAPI.update(school.id, { head_master_user_id: Number(userId) }));
      }
    });

    currentlyAssigned.forEach(school => {
      if (!selected.has(String(school.id))) {
        changes.push(schoolAPI.update(school.id, { head_master_user_id: null }));
      }
    });

    await Promise.all(changes);
  };

  const submit = async event => {
    event.preventDefault();
    if (!form.username.trim() || !form.email.trim()) {
      Swal.fire('Missing details', 'Username and email are required.', 'warning');
      return;
    }
    if (!form.id && !form.password) {
      Swal.fire('Missing password', 'Password is required for a new head master.', 'warning');
      return;
    }

    try {
      const payload = {
        username: form.username.trim(),
        email: form.email.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        status: form.status,
        roles: ['HEAD_MASTER'],
        ...(form.password ? { password: form.password } : {}),
      };
      const saved = form.id
        ? await userAPI.updateUser(form.id, payload)
        : await userAPI.createUser(payload);
      const userId = form.id || saved.data?.id;
      await saveAssignments(userId);
      setModalOpen(false);
      await load();
      Swal.fire('Saved', 'Head master record has been updated.', 'success');
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.error || err.message || 'Please try again.', 'error');
    }
  };

  const deleteHeadMaster = async user => {
    const result = await Swal.fire({
      title: 'Delete Head Master?',
      text: `This will remove ${displayName(user)} from user accounts.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#e74c3c',
    });
    if (!result.isConfirmed) return;
    await userAPI.deleteUser(user.id);
    await load();
    Swal.fire('Deleted', 'Head master has been removed.', 'success');
  };

  const filtered = headMasters.filter(user =>
    `${user.username} ${user.email} ${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      header: 'Head Master',
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ width: 34, height: 34, borderRadius: 8, background: 'rgba(16,172,132,0.12)', color: '#10ac84', display: 'grid', placeItems: 'center' }}><FaUserTie /></span>
          <span><b>{displayName(row.original)}</b><br /><small>{row.original.email}</small></span>
        </div>
      ),
    },
    { header: 'Phone', accessorKey: 'phone' },
    {
      header: 'Assigned Schools',
      cell: ({ row }) => assignedByUser.get(Number(row.original.id))?.length || 0,
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          <button className="eduss-global-action-btn edit" onClick={() => openEdit(row.original)}><FaEdit /></button>
          <button className="eduss-global-action-btn delete" onClick={() => deleteHeadMaster(row.original)}><FaTrash /></button>
        </div>
      ),
    },
  ];

  return (
    <div className="premium-card">
      <TableToolbar
        title="Head Masters"
        subtitle="Manage head master portal users and attach schools"
        onSearch={setSearch}
        onAdd={openCreate}
        addText="Add Head Master"
        showImportExport={false}
      />
      <div style={{ padding: '0 30px 30px' }}>
        <DataTable data={filtered} columns={columns} loading={loading} />
      </div>

      {modalOpen && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="eduss-global-modal large eduss-admin-form-modal" onClick={event => event.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>{form.id ? 'Edit Head Master' : 'Add Head Master'}</h3>
              <button onClick={() => setModalOpen(false)}>&times;</button>
            </div>
            <form className="eduss-global-modal-content" onSubmit={submit}>
              <div className="ifrm-grid">
                <label className="ifrm-field"><span className="ifrm-label">Username</span><input className="ifrm-input" name="username" value={form.username} onChange={change} /></label>
                <label className="ifrm-field"><span className="ifrm-label">Email</span><input className="ifrm-input" name="email" value={form.email} onChange={change} /></label>
                <label className="ifrm-field"><span className="ifrm-label">First Name</span><input className="ifrm-input" name="firstName" value={form.firstName} onChange={change} /></label>
                <label className="ifrm-field"><span className="ifrm-label">Last Name</span><input className="ifrm-input" name="lastName" value={form.lastName} onChange={change} /></label>
                <label className="ifrm-field"><span className="ifrm-label">Phone</span><input className="ifrm-input" name="phone" value={form.phone} onChange={change} /></label>
                <label className="ifrm-field"><span className="ifrm-label">{form.id ? 'New Password' : 'Password'}</span><input className="ifrm-input" name="password" type="password" value={form.password} onChange={change} /></label>
              </div>

              <div className="ifrm-section" style={{ marginTop: 18 }}>
                <div className="ifrm-section-title"><FaSchool /> Attached Schools</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, maxHeight: 240, overflow: 'auto' }}>
                  {schools.map(school => (
                    <label key={school.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 10, border: '1px solid #e5e7eb', borderRadius: 8 }}>
                      <input type="checkbox" checked={form.schoolIds.includes(String(school.id))} onChange={() => toggleSchool(school.id)} />
                      <span>{school.school_name || school.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="ifrm-actions">
                <button type="button" className="ifrm-btn ifrm-btn-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
                <button type="submit" className="ifrm-btn ifrm-btn-submit"><FaPlus /> Save Head Master</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
